import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  Award,
  Check,
  Copy,
  Mail,
  MapPin,
  Share2,
  Trophy,
  Users,
  Target,
  Flame,
  Printer,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import AmbassadorCertificate from "../components/AmbassadorCertificate";

/**
 * /my-pledge/:code
 *
 * Personal dashboard for a charter supporter. Shows their status, their
 * referral performance, their team, a progress checklist, and offers a
 * printable supporter certificate.
 */
export default function MyPledge() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | not_found

  useEffect(() => {
    let cancelled = false;
    const upper = (code || "").toUpperCase();
    api
      .get(`/credit-union/supporter/${encodeURIComponent(upper)}`)
      .then((r) => {
        if (!cancelled) {
          setData(r.data);
          setStatus("ready");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("not_found");
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (status === "loading") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="my-pledge-loading">
        <div className="text-xs uppercase tracking-widest text-bdu-cream/50 animate-pulse">
          Loading your dashboard…
        </div>
      </div>
    );
  }

  if (status === "not_found") return <NotFoundView code={code} />;

  return <SupporterDashboard data={data} />;
}

// ---------- Dashboard ----------

function SupporterDashboard({ data }) {
  const share = useShareLink(data.my_referral_code);
  const toNext = data.next_tier_at
    ? Math.max(0, data.next_tier_at - data.referred_count)
    : 0;
  const nextTier = (data.next_tier_name || "").toLowerCase() || "bronze";
  const progressPct =
    data.next_tier_at && data.next_tier_at > 0
      ? Math.min(100, Math.round((data.referred_count / data.next_tier_at) * 100))
      : 100;

  return (
    <div className="grain" data-testid="my-pledge-dashboard">
      {/* HERO */}
      <section
        className="relative overflow-hidden border-b border-bdu-gold/10"
        data-testid="my-pledge-hero"
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(1100px 450px at 20% 20%, rgba(212,175,55,0.3), transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="label-eyebrow flex items-center gap-2">
              <Trophy size={12} className="text-bdu-gold" /> Charter Supporter · Member {data.member_number}
            </div>
            <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] text-bdu-cream tracking-tight">
              Welcome back,
              <br />
              <span className="text-bdu-gold italic">{data.first_name}</span>.
            </h1>
            <p className="mt-6 text-lg text-bdu-cream/75 max-w-xl leading-relaxed">
              Your pledge is on the charter roll. This is your private
              dashboard — track your tier, your referrals, and the next
              milestone within reach.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <div
              className="border border-bdu-gold/50 bg-bdu-gold/5 p-6 w-full max-w-sm"
              data-testid="my-pledge-tier-card"
            >
              <div className="text-[10px] uppercase tracking-[0.26em] text-bdu-cream/60">
                Current Tier
              </div>
              <div className="mt-2 flex items-end justify-between">
                <div>
                  <div className="font-serif text-4xl text-bdu-gold leading-none">
                    {data.tier || "ENROLLED"}
                  </div>
                  <div className="mt-2 text-sm text-bdu-cream/70">
                    {data.referred_count} referral
                    {data.referred_count === 1 ? "" : "s"}
                  </div>
                </div>
                <Award size={44} className="text-bdu-gold opacity-50" />
              </div>
              {data.next_tier_at && (
                <div className="mt-5">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-bdu-cream/55">
                    <span>Next: {nextTier}</span>
                    <span>{data.referred_count} / {data.next_tier_at}</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-black/70 border border-bdu-gold/15 overflow-hidden">
                    <div
                      className="h-full bg-bdu-gold transition-all duration-700"
                      style={{ width: `${progressPct}%` }}
                      data-testid="my-pledge-tier-progress"
                    />
                  </div>
                  <div className="mt-2 text-[11px] text-bdu-cream/55">
                    {toNext > 0
                      ? `${toNext} more referral${toNext === 1 ? "" : "s"} unlocks the next tier`
                      : "Tier unlocked — a new tier badge is on the way."}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* STATS ROW */}
      <section className="border-b border-bdu-gold/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatTile
            label="Pledge Status"
            value={data.payment_status === "paid" ? "Paid" : "Committed"}
            sub={`$${(data.pledge_amount || 0).toLocaleString()} on file`}
            testId="my-pledge-stat-status"
          />
          <StatTile
            label="FOM"
            value={data.in_fom ? data.fom_county || "In FOM" : "Out of FOM"}
            sub={data.in_fom ? `${data.city || ""} ${data.state || ""}` : "Not counted toward 3,000"}
            testId="my-pledge-stat-fom"
            gold={data.in_fom}
          />
          <StatTile
            label="Referred"
            value={data.referred_count}
            sub={`$${(data.referred_pledged || 0).toLocaleString()} brought in`}
            testId="my-pledge-stat-referred"
          />
          <StatTile
            label="In-FOM Referrals"
            value={data.referred_in_fom_count}
            sub="count toward charter"
            testId="my-pledge-stat-fom-referrals"
            gold
          />
        </div>
      </section>

      {/* SHARE / RECRUIT */}
      <section className="border-b border-bdu-gold/10 bg-black/30">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-eyebrow flex items-center gap-2">
              <Share2 size={12} className="text-bdu-gold" /> Grow the Roll
            </div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream leading-tight">
              Your personal <span className="text-bdu-gold italic">referral link</span>.
            </h2>
            <p className="mt-5 text-bdu-cream/70 leading-relaxed">
              Every pledge made with your link gets you one step closer to the
              next tier. Share it in a text message, in a sermon handout, on
              Instagram — the credit union counts them all.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ReferralLinkBox share={share} />
            <div className="mt-4 flex flex-wrap gap-2">
              <ShareBtn
                label="SMS"
                href={`sms:?body=${encodeURIComponent(share.message)}`}
                testId="my-pledge-share-sms"
              />
              <ShareBtn
                label="Email"
                href={`mailto:?subject=${encodeURIComponent("Help build a Black-owned credit union")}&body=${encodeURIComponent(share.message)}`}
                testId="my-pledge-share-email"
              />
              <ShareBtn
                label="Twitter / X"
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(share.message)}`}
                testId="my-pledge-share-twitter"
              />
              <ShareBtn
                label="LinkedIn"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(share.url)}`}
                testId="my-pledge-share-linkedin"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CHECKLIST + TEAM */}
      <section className="border-b border-bdu-gold/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="label-eyebrow flex items-center gap-2">
              <Target size={12} className="text-bdu-gold" /> Your Next Steps
            </div>
            <ul className="mt-6 space-y-3" data-testid="my-pledge-checklist">
              {(data.checklist || []).map((it) => (
                <ChecklistItem key={it.key} item={it} />
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            {data.team ? (
              <TeamCard team={data.team} />
            ) : (
              <NoTeamCard />
            )}

            {/* Recent referrals */}
            {data.recent_referrals && data.recent_referrals.length > 0 && (
              <div
                className="mt-6 border border-bdu-gold/15 bg-black/40 p-5"
                data-testid="my-pledge-recent-referrals"
              >
                <div className="label-eyebrow">Recent Referrals</div>
                <ul className="mt-3 space-y-2">
                  {data.recent_referrals.slice(0, 5).map((r, i) => (
                    <li
                      key={`${r.first_name}-${i}`}
                      className="flex items-center justify-between text-xs text-bdu-cream/75"
                    >
                      <div className="flex items-center gap-2">
                        <Flame size={11} className="text-bdu-gold" />
                        <span className="text-bdu-cream">{r.first_name}</span>
                        <span className="text-bdu-cream/50">from {r.city}</span>
                      </div>
                      <span className="text-bdu-gold font-mono">
                        ${(r.pledge_amount || 0).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CERTIFICATE */}
      <section className="border-b border-bdu-gold/10 bg-black/30" data-testid="my-pledge-certificate-section">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-14">
          <div className="label-eyebrow flex items-center gap-2">
            <Printer size={12} className="text-bdu-gold" /> Printable Keepsake
          </div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream leading-tight max-w-2xl">
            Your Charter Supporter Certificate.
          </h2>
          <p className="mt-4 text-bdu-cream/65 max-w-2xl leading-relaxed">
            Print it. Frame it. Or share it. Every tier you earn upgrades the
            certificate.
          </p>
          <div className="mt-8">
            <AmbassadorCertificate
              name={data.full_name}
              tier={{
                key: (data.tier || "founding").toLowerCase(),
                name: data.tier || "FOUNDING",
              }}
              dollars={data.pledge_amount || 0}
              code={data.my_referral_code}
              memberSince={data.signed_at}
            />
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="py-16 text-center">
        <Link
          to="/credit-union#leaderboard"
          className="btn-ghost px-7 py-3.5 inline-flex"
          data-testid="my-pledge-cta-leaderboard"
        >
          <Trophy size={16} /> See the Leaderboard
          <ArrowRight size={14} />
        </Link>
        <p className="mt-10 text-[11px] text-bdu-cream/40 italic max-w-xl mx-auto leading-relaxed px-6">
          Proposed Credit Union · pending NCUA charter approval · pledges are
          non-binding and not deposits. Pledges held in escrow at
          Citizen Trust Bank.
        </p>
      </section>
    </div>
  );
}

// ---------- Pieces ----------

function StatTile({ label, value, sub, gold, testId }) {
  return (
    <div
      data-testid={testId}
      className={`border p-4 ${gold ? "border-bdu-gold/50 bg-bdu-gold/5" : "border-bdu-gold/15 bg-black/30"}`}
    >
      <div className="text-[9px] uppercase tracking-[0.26em] text-bdu-cream/55">
        {label}
      </div>
      <div
        className={`mt-2 font-serif text-2xl ${gold ? "text-bdu-gold" : "text-bdu-cream"} leading-tight`}
      >
        {value}
      </div>
      <div className="mt-1 text-[11px] text-bdu-cream/55 leading-tight">{sub}</div>
    </div>
  );
}

function ReferralLinkBox({ share }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      toast.error("Clipboard blocked — long-press to copy manually");
    }
  };

  return (
    <div
      className="flex items-stretch border border-bdu-gold/40 bg-black/60"
      data-testid="my-pledge-referral-link-box"
    >
      <code
        className="flex-1 font-mono text-sm text-bdu-cream/85 px-4 py-3 truncate"
        data-testid="my-pledge-referral-link"
      >
        {share.url}
      </code>
      <button
        type="button"
        onClick={copy}
        className="flex-shrink-0 px-4 flex items-center gap-2 text-xs uppercase tracking-widest font-semibold bg-bdu-gold text-black hover:brightness-110 transition"
        data-testid="my-pledge-copy-btn"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function ShareBtn({ label, href, testId }) {
  return (
    <a
      href={href}
      target={label !== "SMS" && label !== "Email" ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="btn-ghost text-xs px-4 py-2 flex items-center gap-2"
      data-testid={testId}
    >
      {label}
    </a>
  );
}

function ChecklistItem({ item }) {
  return (
    <li
      className={`flex items-start gap-3 border px-4 py-3 ${
        item.done ? "border-bdu-gold/40 bg-bdu-gold/5" : "border-bdu-gold/15 bg-black/30"
      }`}
      data-testid={`my-pledge-check-${item.key}`}
    >
      <div
        className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
          item.done ? "bg-emerald-500" : "border border-bdu-cream/30"
        }`}
      >
        {item.done ? <Check size={13} className="text-white" strokeWidth={3} /> : null}
      </div>
      <div className="flex-1">
        <div
          className={`text-sm ${item.done ? "text-bdu-cream line-through decoration-bdu-gold/30" : "text-bdu-cream"}`}
        >
          {item.label}
        </div>
      </div>
    </li>
  );
}

function TeamCard({ team }) {
  return (
    <div
      className="border border-bdu-gold/40 bg-bdu-gold/5 p-5"
      data-testid="my-pledge-team-card"
    >
      <div className="flex items-start gap-3">
        <Users size={18} className="text-bdu-gold mt-1" />
        <div>
          <div className="label-eyebrow">Your Team</div>
          <h3 className="mt-1 font-serif text-xl text-bdu-cream leading-tight">
            {team.name}
          </h3>
          <div className="mt-1 text-[11px] uppercase tracking-widest text-bdu-cream/55">
            {team.type} · Captain: {team.captain_name}
          </div>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
        <div className="border border-bdu-gold/20 bg-black/30 p-3">
          <div className="text-[9px] uppercase tracking-widest text-bdu-cream/55">
            Teammates
          </div>
          <div className="mt-1 font-serif text-xl text-bdu-cream">
            {team.member_count}
          </div>
        </div>
        <div className="border border-bdu-gold/20 bg-black/30 p-3">
          <div className="text-[9px] uppercase tracking-widest text-bdu-cream/55">
            Team Pledged
          </div>
          <div className="mt-1 font-serif text-xl text-bdu-gold">
            ${(team.total_pledged || 0).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}

function NoTeamCard() {
  return (
    <div
      className="border border-bdu-gold/15 bg-black/30 p-5"
      data-testid="my-pledge-no-team"
    >
      <div className="flex items-start gap-3">
        <Users size={18} className="text-bdu-cream/40 mt-1" />
        <div>
          <div className="label-eyebrow">No Team Yet</div>
          <h3 className="mt-1 font-serif text-xl text-bdu-cream leading-tight">
            Rally your church, HBCU, or Greek line
          </h3>
          <p className="mt-2 text-sm text-bdu-cream/60 leading-relaxed">
            Pledging as a team compounds referrals and locks your name onto a
            group leaderboard that NCUA reviewers see.
          </p>
          <Link
            to="/credit-union#teams"
            className="btn-ghost text-xs mt-4 inline-flex items-center gap-2"
            data-testid="my-pledge-join-team-link"
          >
            Join or Create a Team <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function NotFoundView({ code }) {
  return (
    <div
      className="min-h-[70vh] flex items-center justify-center px-6"
      data-testid="my-pledge-not-found"
    >
      <div className="max-w-lg text-center">
        <div className="label-eyebrow">Supporter Not Found</div>
        <h1 className="mt-5 font-serif text-4xl text-bdu-cream">
          We couldn't find <code className="text-bdu-gold">{(code || "").toUpperCase()}</code>
        </h1>
        <p className="mt-5 text-bdu-cream/65 leading-relaxed">
          Double-check the code on your welcome email or pledge card. If you're
          brand new, start by pledging on the credit union page.
        </p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link to="/credit-union" className="btn-gold px-6 py-3" data-testid="my-pledge-not-found-cta">
            Pledge Now <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

// ---------- helpers ----------

function useShareLink(code) {
  return useMemo(() => {
    const base =
      (typeof window !== "undefined" ? window.location.origin : "https://blackdollarsunited.org") +
      "/credit-union?ref=" +
      (code || "");
    const message = `I just pledged to the proposed Trillion Dollar Spending Power Federal Credit Union. Join the charter roll — ${base}`;
    return { url: base, message };
  }, [code]);
}
